import React, { useMemo } from "react";

import {
  getSystematicRepechagePodiumPreview,
} from "./tableauRepechageSystematique/systematicRepechageUtils";

const Backdrop = ({ children }) => (
  <div className="fixed inset-0 z-[999] flex items-center justify-center p-4">
    <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" />
    <div className="relative w-full max-w-2xl">{children}</div>
  </div>
);

const accents = {
  gold: {
    bg: "bg-amber-50",
    border: "border-amber-200",
    iconBg: "bg-amber-100",
    title: "text-amber-700",
  },
  silver: {
    bg: "bg-slate-50",
    border: "border-slate-200",
    iconBg: "bg-slate-100",
    title: "text-slate-700",
  },
  bronze: {
    bg: "bg-orange-50",
    border: "border-orange-200",
    iconBg: "bg-orange-100",
    title: "text-orange-700",
  },
};

const MedalCard = ({ medal, title, competitor, accent }) => {
  const isPending = !competitor;

  return (
    <div
      className={`rounded-2xl border p-4 shadow-sm flex items-center gap-4 ${accent.bg} ${accent.border}`}
    >
      <div
        className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full text-2xl ${accent.iconBg}`}
      >
        {medal}
      </div>

      <div className="min-w-0 flex-1">
        <p className={`text-sm font-semibold ${accent.title}`}>{title}</p>

        <p className="truncate text-base font-bold text-slate-900">
          {isPending
            ? "(en attente des combats)"
            : `${competitor.nom_competiteur ?? ""} ${
                competitor.prenom_competiteur ?? ""
              }`}
        </p>

        <p className="truncate text-sm text-gray-500">
          {isPending ? "\u00A0" : competitor.club_competiteur || "\u00A0"}
        </p>
      </div>
    </div>
  );
};

const ClassementSystematiqueRepechageModal = ({
  isOpen,
  onClose,
  firstRoundSeed = [],
  competiteurs = [],
  combats = [],
  roundNames = [],
}) => {
  const byId = useMemo(
    () => new Map(competiteurs.map((c) => [Number(c.id), c])),
    [competiteurs],
  );

  const preview = useMemo(
    () =>
      getSystematicRepechagePodiumPreview({
        firstRoundSeed,
        competiteurs,
        combats,
        roundNames,
      }),
    [firstRoundSeed, competiteurs, combats, roundNames],
  );

  if (!isOpen) return null;

  const first = preview?.champion ? byId.get(Number(preview.champion)) : null;
  const second = preview?.runnerUp ? byId.get(Number(preview.runnerUp)) : null;
  const thirdA = preview?.bronzeA ? byId.get(Number(preview.bronzeA)) : null;
  const thirdB = preview?.bronzeB ? byId.get(Number(preview.bronzeB)) : null;

  return (
    <Backdrop>
      <div className="overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-xl dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div>
            <h3 className="text-lg font-bold text-slate-900 dark:text-white">
              Classement — Repêchage systématique
            </h3>
            <p className="mt-1 text-xs text-slate-500 dark:text-slate-400">
              Le classement est complet lorsque la finale et les deux combats de
              3e place sont saisis.
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-slate-100 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-200 dark:bg-white/10 dark:text-white dark:hover:bg-white/15"
          >
            Fermer
          </button>
        </div>

        <div className="space-y-4 p-6">
          <MedalCard
            medal="🥇"
            title="1er"
            competitor={first}
            accent={accents.gold}
          />

          <MedalCard
            medal="🥈"
            title="2e"
            competitor={second}
            accent={accents.silver}
          />

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <MedalCard
              medal="🥉"
              title="3e"
              competitor={thirdA}
              accent={accents.bronze}
            />

            <MedalCard
              medal="🥉"
              title="3e"
              competitor={thirdB}
              accent={accents.bronze}
            />
          </div>
        </div>
      </div>
    </Backdrop>
  );
};

export default ClassementSystematiqueRepechageModal;